/**
 * ChatMessageBubble — Feb 2026
 * ============================
 *
 * A single chat turn in the AvA practice thread. Handles:
 *   • user / AvA / system role styling
 *   • the [CHALLENGE:slug] token → tappable chip rendering
 *   • the TTS-synced word-reveal drip for AvA messages
 *   • copy-to-clipboard on AvA messages (hover-reveal button)
 *   • delete-this-turn on user + AvA messages (hover-reveal button)
 *
 * Extracted from `ConversationsChat.js` (Feb 2026 refactor). Zero
 * behaviour change — every `data-testid` and every visual class stays
 * identical, so existing e2e tests remain valid.
 */
import React from 'react';
import { Copy, Check, Trash2, Target, Volume2 } from 'lucide-react';
import { CHALLENGE_TOKEN_RE, extractChallengeTokens, prettySlug } from '../../utils/challengeTokens';

const formatTime = (date) =>
  (date instanceof Date ? date : new Date(date))
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const ChatMessageBubble = ({
  msg,
  selectedAva,
  interactionMode,
  revealingMsgId,
  revealedWords,
  copiedMsgId,
  onCopy,
  onSpeak,
  onDelete,
  onChallengeClick,
}) => {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';
  const isAva = msg.role === 'ava';

  // Text to render inside the bubble. AvA messages honour the
  // TTS-synced word drip; user/system messages render verbatim.
  const bodyText = (() => {
    if (isAva && revealingMsgId === msg.id) {
      return (msg.content || '')
        .split(/\s+/)
        .slice(0, revealedWords)
        .join(' ')
        .replace(CHALLENGE_TOKEN_RE, '')
        .trim();
    }
    if (isAva) return (msg.content || '').replace(CHALLENGE_TOKEN_RE, '').trim();
    return msg.content;
  })();

  const challengeSlugs = isAva ? extractChallengeTokens(msg.content) : [];

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {isAva && selectedAva?.avatar_url && (
        <img
          src={selectedAva.avatar_url}
          alt=""
          className="w-8 h-8 rounded-full mr-3 flex-shrink-0 object-cover"
        />
      )}
      <div
        className={`relative group max-w-[85%] px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl ${
          isUser
            ? 'bg-purple-500 text-white rounded-br-md'
            : isSystem
              ? 'bg-red-100 text-red-700 rounded-bl-md'
              : 'bg-white shadow-md text-gray-900 rounded-bl-md'
        }`}
      >
        <p className="whitespace-pre-wrap">{bodyText}</p>

        {challengeSlugs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid={`challenge-chips-${msg.id}`}>
            {challengeSlugs.map((slug, i) => (
              <button
                key={`${slug}-${i}`}
                onClick={() => onChallengeClick && onChallengeClick(slug)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 text-xs font-semibold shadow-sm transition"
                data-testid={`challenge-chip-${slug}`}
              >
                <Target size={12} />
                Try: {prettySlug(slug)}
              </button>
            ))}
          </div>
        )}

        <p className={`text-xs mt-1 ${isUser ? 'text-purple-200' : 'text-gray-400'}`}>
          {formatTime(msg.timestamp)}
        </p>

        {isAva && (
          <button
            onClick={() => onCopy && onCopy(msg)}
            className="mt-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-opacity inline-flex"
            data-testid={`copy-msg-${msg.id}`}
            title="Copy message"
          >
            {copiedMsgId === msg.id
              ? <Check size={18} className="text-green-500" />
              : <Copy size={18} className="text-gray-400" />}
          </button>
        )}
        {isAva && onSpeak && !msg.blocked && (
          <button
            onClick={() => onSpeak(msg)}
            className="mt-2 ml-1 p-1.5 rounded-lg hover:bg-purple-50 transition-colors inline-flex"
            data-testid={`speak-msg-${msg.id}`}
            title="Hear this out loud"
          >
            <Volume2 size={18} className="text-purple-400" />
          </button>
        )}

        {!isSystem && (
          <button
            onClick={() => onDelete && onDelete(msg)}
            className={`absolute bottom-1 right-1 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
              isUser ? 'hover:bg-purple-600 text-purple-200' : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
            }`}
            data-testid={`delete-msg-${msg.id}`}
            title="Delete this turn"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatMessageBubble;
